import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Search, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Hint } from '@/components/Hint';
import { Button, Card, CardContent, CardHeader, Input, Textarea } from '@/components/ui';
import { curationApi, type EntitySearchHit, type ManualTripletInput } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/i18n/useApiError';

type ObjectMode = 'iri' | 'literal';

interface FormState {
  subjectIri: string;
  predicateIri: string;
  objectMode: ObjectMode;
  objectIri: string;
  objectLiteral: string;
  objectDatatype: string;
  rationale: string;
}

const INITIAL_STATE: FormState = {
  subjectIri: '',
  predicateIri: '',
  objectMode: 'iri',
  objectIri: '',
  objectLiteral: '',
  objectDatatype: '',
  rationale: '',
};

/**
 * Manual curation (DEC-11 / M-type entry).
 *
 * Triplets entered here land in the error queue as `M` items; the analyst
 * confirms them through the standard ErrorDetail review, which produces the
 * training signal and (post-MVP) materialises into staging for the next
 * morning commit.
 */
export function ManualCuration() {
  const { t } = useTranslation(['studio', 'common']);
  const apiError = useApiError();
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSubmittedId(null);
  };

  const payload = useMemo<ManualTripletInput | null>(() => {
    if (!form.subjectIri.trim() || !form.predicateIri.trim() || !form.rationale.trim()) {
      return null;
    }
    const base: ManualTripletInput = {
      subject_iri: form.subjectIri.trim(),
      predicate_iri: form.predicateIri.trim(),
      rationale: form.rationale.trim(),
    };
    if (form.objectMode === 'iri') {
      if (!form.objectIri.trim()) return null;
      base.object_iri = form.objectIri.trim();
    } else {
      if (!form.objectLiteral.trim()) return null;
      base.object_literal = form.objectLiteral;
      if (form.objectDatatype.trim()) base.object_datatype = form.objectDatatype.trim();
    }
    return base;
  }, [form]);

  const mutation = useMutation({
    mutationFn: (body: ManualTripletInput) => curationApi.submitManual(body),
    onSuccess: (data) => {
      setSubmittedId(data.error_id);
      setForm(INITIAL_STATE);
      // Refresh the error queue so the new M-type row is visible immediately.
      queryClient.invalidateQueries({ queryKey: ['errors', 'list'] });
    },
  });

  const mutationError = apiError(mutation.error);

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">{t('studio:curation.title')}</h1>
        <p className="mt-1 text-body text-slate-600">{t('studio:curation.subtitle')}</p>
      </header>

      <Card>
        <CardHeader>
          <h3 className="flex items-center gap-1.5 text-h3 text-brand-900">
            {t('studio:curation.subject')}
            <Hint k="iri" />
          </h3>
        </CardHeader>
        <CardContent>
          <EntityPicker
            value={form.subjectIri}
            onChange={(iri) => update('subjectIri', iri)}
            placeholder="https://energytransition.kg/data/..."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">{t('studio:curation.predicate')}</h3>
        </CardHeader>
        <CardContent>
          <Input
            value={form.predicateIri}
            onChange={(e) => update('predicateIri', e.target.value)}
            placeholder="https://energytransition.kg/ontology#hasCapacity"
            className="font-mono"
          />
          <p className="mt-1 text-small text-slate-500">
            {t('studio:curation.predicateHint')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-h3 text-brand-900">{t('studio:curation.object')}</h3>
            <div className="flex items-center gap-1 rounded border border-slate-200 p-0.5 text-small">
              <ModeToggle
                active={form.objectMode === 'iri'}
                onClick={() => update('objectMode', 'iri')}
              >
                {t('studio:curation.objectIri')}
              </ModeToggle>
              <ModeToggle
                active={form.objectMode === 'literal'}
                onClick={() => update('objectMode', 'literal')}
              >
                {t('studio:curation.objectLiteral')}
              </ModeToggle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {form.objectMode === 'iri' ? (
            <EntityPicker
              value={form.objectIri}
              onChange={(iri) => update('objectIri', iri)}
              placeholder="https://energytransition.kg/data/..."
            />
          ) : (
            <div className="space-y-2">
              <Input
                value={form.objectLiteral}
                onChange={(e) => update('objectLiteral', e.target.value)}
                placeholder='e.g. "Project Alpha" or 150.5'
              />
              <Input
                value={form.objectDatatype}
                onChange={(e) => update('objectDatatype', e.target.value)}
                placeholder={t('studio:curation.objectDatatype')}
                className="font-mono text-small"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-h3 text-brand-900">{t('studio:curation.rationale')}</h3>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.rationale}
            onChange={(e) => update('rationale', e.target.value)}
            placeholder={t('studio:curation.rationalePlaceholder')}
            rows={4}
          />
        </CardContent>
      </Card>

      {submittedId && (
        <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-small text-emerald-800">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          <span>
            {t('studio:curation.submitted', { id: submittedId.slice(0, 8) })}
          </span>
        </div>
      )}

      {mutationError && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-small text-red-800">
          <XCircle className="h-4 w-4" aria-hidden />
          <span>{mutationError}</span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setForm(INITIAL_STATE);
            setSubmittedId(null);
          }}
          disabled={mutation.isPending}
        >
          {t('common:actions.clear')}
        </Button>
        <Button
          type="button"
          onClick={() => payload && mutation.mutate(payload)}
          disabled={!payload || mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />}
          {t('studio:curation.submit')}
        </Button>
      </div>
    </div>
  );
}

function ModeToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-2 py-1 text-small transition',
        active ? 'bg-brand-100 text-brand-800' : 'text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}

// ─── Entity picker with autocomplete ──────────────────────────────

function EntityPicker({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (iri: string) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation(['studio', 'common']);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  // Debounce: only query when user pauses typing for 200ms.
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const tm = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(tm);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['curation', 'search', debounced],
    queryFn: () => curationApi.searchEntities(debounced),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  });

  return (
    <div className="space-y-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-mono text-small"
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" aria-hidden />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            placeholder={t('studio:curation.entityPickerPlaceholder')}
            className="flex-1"
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
          />
        </div>
        {open && debounced.length >= 2 && (
          <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg">
            {isLoading && (
              <div className="flex items-center gap-2 p-2 text-small text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                {t('common:status.loading')}
              </div>
            )}
            {!isLoading && data && data.length === 0 && (
              <div className="p-2 text-small text-slate-500">{t('common:status.noResults')}</div>
            )}
            {!isLoading &&
              data?.map((hit: EntitySearchHit) => (
                <button
                  key={hit.entity_iri}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(hit.entity_iri);
                    setSearch(hit.label);
                    setOpen(false);
                  }}
                  className="block w-full px-2 py-1.5 text-left hover:bg-slate-50"
                >
                  <div className="text-small font-medium text-slate-800">{hit.label}</div>
                  <div className="truncate font-mono text-small text-slate-500">
                    {hit.entity_iri}
                  </div>
                  <div className="text-small text-slate-400">{hit.entity_class}</div>
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
